import { Box } from '@mui/system';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Typography } from '@mui/material';
import { PasswordFormField } from 'components/auth/PasswordFormField';
import { ILoginValues } from 'components/auth/types';
import AuthModal from 'components/system/AuthModal/AuthModal';
import { LoadingButton, OutlinedButton } from 'components/system/buttons';
import { FormTextField } from 'components/system/form';
import { TechnologyLogo } from 'components/system/TechnologyLogo/TechnologyLogo';
import { SESSION_STATE } from 'providers/SessionProvider/sessionProvider.constants';
import { TSessionState } from 'providers/SessionProvider/sessionProvider.types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useYupValidationResolver from 'utils/useYupValidationResolver';
import { FORM_ERROR_MESSAGE, MAX_LENGTH, requiredEmailField } from 'yupHelper';
import { useExpiredSessionLogin } from '../hooks/useExpiredSessionLogin';

const sessionExpiredStates: TSessionState[] = [
  SESSION_STATE.EXPIRED,
  SESSION_STATE.LOGGING_OUT,
  SESSION_STATE.EXPIRED_LOGIN_PENDING_ACTION,
];

const validationSchema = yup.object({
  email: requiredEmailField,
  password: yup
    .string()
    .max(MAX_LENGTH.PASSWORD, FORM_ERROR_MESSAGE.MAX)
    .required(FORM_ERROR_MESSAGE.REQUIRED),
});

export const ExpiredSessionModal = () => {
  const {
    loading,
    formHandleSubmit,
    handleForgotPassword,
    handleChangeAccount,
    prefilledEmail,
    setPrefilledEmail,
    open,
    sessionState,
  } = useExpiredSessionLogin();

  const {
    register,
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
    setValue,
    reset,
  } = useForm<ILoginValues>({
    resolver: useYupValidationResolver(validationSchema),
    defaultValues: {
      email: prefilledEmail || '',
      password: '',
    },
  });

  const { pathname } = useLocation();

  // Reset form (except prefilledEmail) after successful submission
  // This is important to clear the password but not the email
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ email: prefilledEmail || '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (prefilledEmail) {
      setValue('email', prefilledEmail);
    }
  }, [prefilledEmail, setValue]);

  // Clear the email if the user returns to the login page
  useEffect(() => {
    if (pathname === '/') {
      setPrefilledEmail('');
      setValue('email', '');
    }
  }, [pathname, setPrefilledEmail, setValue]);

  const isSessionExpired = sessionExpiredStates.includes(sessionState);

  return (
    <AuthModal open={open}>
      <AuthModal.Logo>
        <TechnologyLogo />
      </AuthModal.Logo>

      <AuthModal.Content>
        <AuthModal.Title
          sx={{
            marginBottom: isSessionExpired ? 4 : 12,
          }}
        >
          Log In
        </AuthModal.Title>
        {isSessionExpired && (
          <Typography variant="h6" component="p" sx={{ marginBottom: 10 }}>
            Your session has expired. Please log in again.
          </Typography>
        )}
        <Box>
          <FormTextField
            id="email"
            label="Email"
            variant="standard"
            error={errors.email?.message}
            required
            InputProps={{ readOnly: Boolean(prefilledEmail) }}
            {...register('email')}
            sx={{ marginBottom: 10 }}
          />
          <PasswordFormField
            onForgotPassword={handleForgotPassword}
            id="password"
            label="Password"
            variant="standard"
            error={errors.password?.message}
            required
            {...register('password')}
          />
        </Box>
      </AuthModal.Content>
      <AuthModal.Buttons>
        {isSessionExpired && (
          <OutlinedButton
            disabled={loading}
            onClick={handleChangeAccount}
            sx={{ paddingX: 7 }}
          >
            Change account
          </OutlinedButton>
        )}
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={handleSubmit(formHandleSubmit)}
        >
          Log In
        </LoadingButton>
      </AuthModal.Buttons>
    </AuthModal>
  );
};
